import { useState, useMemo, useEffect } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Chip, Button, IconButton, TextField, RadioGroup,
  FormControlLabel, Radio, Alert, CircularProgress,
} from '@mui/material';
import CloseIcon       from '@mui/icons-material/Close';
import LockOutlineIcon from '@mui/icons-material/LockOutlined';

const STATUS_COLOR = {
  compliant: '#4caf50', partial: '#ffeb3b', non_compliant: '#f44336',
  na: '#7a8fa6', not_assessed: '#4a5a70',
};
const STATUS_LABEL = {
  compliant: 'Compliant', partial: 'Partial', non_compliant: 'Non-Compliant',
  na: 'N/A', not_assessed: 'Not Assessed',
};

export default function SspExportDialog({
  open, onClose, parent, items, exporting, onExport,
}) {
  const [format, setFormat]     = useState('xlsx');
  const [signedBy, setSignedBy] = useState('');

  useEffect(() => {
    if (open) {
      setFormat(parent?.filename?.toLowerCase().endsWith('.xlsx') ? 'xlsx' : 'oscal');
      setSignedBy('');
    }
  }, [open, parent]);

  const totals = useMemo(() => {
    const t = { total: items.length, compliant: 0, partial: 0, non_compliant: 0, na: 0, not_assessed: 0 };
    items.forEach(i => { t[i.status] = (t[i.status] || 0) + 1; });
    return t;
  }, [items]);

  const xlsxDisabled = !parent || !parent.filename?.toLowerCase().endsWith('.xlsx');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#0d1825', border: '1px solid #1e3a5f' } }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid #1e3a5f', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ color: '#ffffff', fontSize: 16, fontWeight: 700 }}>Export SSP</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#7a8fa6' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '20px' }}>
        <Typography sx={{ color: '#7a8fa6', fontSize: 12, mb: 2 }}>
          Generate a new SSP version from the current register. The output is a point-in-time render — your uploaded original stays untouched.
        </Typography>

        {/* Snapshot summary */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, mb: 3 }}>
          {[
            { val: totals.compliant,     label: 'Compliant',    color: STATUS_COLOR.compliant },
            { val: totals.partial,       label: 'Partial',      color: STATUS_COLOR.partial },
            { val: totals.non_compliant, label: 'Non-Comp.',    color: STATUS_COLOR.non_compliant },
            { val: totals.na,            label: 'N/A',          color: STATUS_COLOR.na },
            { val: totals.not_assessed,  label: 'Not Assessed', color: STATUS_COLOR.not_assessed },
          ].map(s => (
            <Box key={s.label} sx={{ textAlign: 'center', padding: '8px 4px', backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 1 }}>
              <Typography sx={{ color: s.color, fontSize: 20, fontWeight: 700, lineHeight: 1 }}>{s.val}</Typography>
              <Typography sx={{ color: '#7a8fa6', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.3, mt: 0.5 }}>{s.label}</Typography>
            </Box>
          ))}
        </Box>

        {/* Format */}
        <Typography sx={{ color: '#c0cfe0', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          Output format
        </Typography>
        <RadioGroup value={format} onChange={(e) => setFormat(e.target.value)} sx={{ mb: 3 }}>
          <FormControlLabel
            value="xlsx"
            disabled={xlsxDisabled}
            control={<Radio sx={{ color: '#7a8fa6', '&.Mui-checked': { color: '#00bcd4' } }} />}
            label={
              <Box>
                <Typography sx={{ color: xlsxDisabled ? '#4a5a70' : '#c0cfe0', fontSize: 13 }}>
                  eMASS XLSX (preserves source template)
                </Typography>
                <Typography sx={{ color: '#4a5a70', fontSize: 11 }}>
                  {xlsxDisabled ? 'Requires an XLSX upload as the parent.' : 'Drops back into eMASS bulk import.'}
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="oscal"
            control={<Radio sx={{ color: '#7a8fa6', '&.Mui-checked': { color: '#00bcd4' } }} />}
            label={
              <Box>
                <Typography sx={{ color: '#c0cfe0', fontSize: 13 }}>OSCAL JSON</Typography>
                <Typography sx={{ color: '#4a5a70', fontSize: 11 }}>
                  NIST 1.1.x SSP schema, machine-readable.
                </Typography>
              </Box>
            }
          />
        </RadioGroup>

        {parent ? (
          <Alert
            severity="info"
            icon={false}
            sx={{ mb: 3, backgroundColor: '#0a1525', color: '#7a8fa6', fontSize: 11, border: '1px solid #1e3a5f' }}
          >
            Parent SSP: <strong style={{ color: '#c0cfe0' }}>{parent.filename}</strong>
            {' '}(uploaded by {parent.uploaded_by})
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 3, backgroundColor: '#2a2410', color: '#ffeb3b', fontSize: 12 }}>
            No parent SSP attached. OSCAL export will succeed but won't carry a parent UUID.
          </Alert>
        )}

        {/* Sign-off */}
        <Typography sx={{ color: '#c0cfe0', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LockOutlineIcon sx={{ fontSize: 14, color: '#00bcd4' }} />
          ISSM Sign-off
        </Typography>
        <Typography sx={{ color: '#7a8fa6', fontSize: 11, mb: 1, lineHeight: 1.5 }}>
          By typing your name below, you certify the current ODP register reflects an accurate assessment of this system's posture.
          This signature is recorded in the audit log and embedded in the exported document.
        </Typography>
        <TextField
          fullWidth
          size="small"
          autoFocus
          value={signedBy}
          onChange={(e) => setSignedBy(e.target.value)}
          placeholder="Type your full name"
          sx={{
            '& .MuiOutlinedInput-root': {
              color: '#c0cfe0', fontSize: 13,
              '& fieldset': { borderColor: '#1e3a5f' },
              '&:hover fieldset': { borderColor: '#00bcd4' },
              '&.Mui-focused fieldset': { borderColor: '#00bcd4' },
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #1e3a5f', padding: '12px 20px', backgroundColor: '#0a1525' }}>
        <Button onClick={onClose} disabled={exporting} sx={{ color: '#7a8fa6', fontSize: 12 }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onExport(format, signedBy.trim())}
          disabled={exporting || signedBy.trim().length < 3}
          startIcon={exporting ? <CircularProgress size={14} sx={{ color: '#0a0e1a' }} /> : null}
          sx={{
            backgroundColor: '#00bcd4', color: '#000', fontWeight: 700, fontSize: 12,
            '&:hover': { backgroundColor: '#00acc1' },
            '&.Mui-disabled': { backgroundColor: '#1e3a5f', color: '#4a5a70' },
          }}
        >
          {exporting ? 'Generating…' : `Sign & Export ${format.toUpperCase()}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
